import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";
import React, { useEffect, useRef, useState } from "react";
import { fp, hp, spH, spV, wp } from "utils/normalizeV2";
import { getPrefAns, getPrefQns, savePrefAns, getUserDetails } from "appRedux/reducers/profileSlice";
import { router, useLocalSearchParams } from "expo-router";
import { useDispatch, useSelector } from "react-redux";

import Buttons from "components/Buttons";
import ChipsSelect from "./ChipsSelect";
import { Colors } from "colors";
import DropDown from "./DropDown";
import ImageSelectInput from "./ImageSelectInput";
import OptionSelect from "./OptionSelect";
import ParentScrollView from "components/ParentScrollView";
import PreferenceSteps from "./PreferenceSteps";
import SearchableDropDown from "./SearchableDropDown";
import SideProfileComp from "components/profile/SideProfileComp";
import TextAreaInput from "./TextAreaInput";
import VText from "components/VText";
import moment from "moment";
import { showToast } from "utils/ToastUtils";

const MyPreferences = () => {
    const [prefAnswers, setPrefAnswers] = useState({});
    const [prefQuestions, setPrefQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingAns, setLoadingAns] = useState(false);
    const [stepNo, setStepNo] = useState(1);
    const [sectionQns, setSectionQns] = useState([]);
    const [closeDropDown, setCloseDropDown] = useState();
    const [submitLoading, setSubmitLoading] = useState(false);

    const userProfile = useSelector((state) => state.ProfileReducer.profileData);
    const userId = useSelector((state) => state.AuthReducer.userId);

    const dispatch = useDispatch();
    const _scrollRef = useRef();
    const local = useLocalSearchParams();
    const localGender = local?.gender || "female";

    useEffect(() => {
        setLoading(true);
        dispatch(getPrefQns())
            .then((res) => {
                setLoading(false);
                if ([200].includes(res?.payload?.status) && Array.isArray(res?.payload?.data?.data)) {
                    setPrefQuestions(res?.payload?.data?.data);
                    setStepNo(1);
                }
            })
            .catch((err) => {
                setLoading(false);
                console.log("err", err);
            });
    }, []);

    useEffect(() => {
        setLoadingAns(true);
        dispatch(getPrefAns({ userId: userId }))
            .then((res) => {
                setLoadingAns(false);
                if ([200].includes(res?.payload?.status) && Array.isArray(res?.payload?.data?.data?.data)) {
                    setPrefAnswers(res?.payload?.data?.data?.data?.[0]?.answers || {});
                } else {
                    showToast("Error");
                }
            })
            .catch((err) => {
                setLoadingAns(false);
                console.log("err", err);
            });
    }, [userId]);

    useEffect(() => {
        if (stepNo && Array.isArray(prefQuestions)) {
            setSectionQns(prefQuestions?.find((o) => o?.section == stepNo)?.questions || []);
        }
    }, [stepNo, prefQuestions]);

    const onSubmit = async () => {
        setSubmitLoading(true);
        dispatch(
            savePrefAns({
                userId: userId,
                answers: prefAnswers,
            })
        )
            .then((res) => {
                setSubmitLoading(false);
                if ([200].includes(res?.payload?.status)) {
                    dispatch(getUserDetails({ userId: userId }));
                    showToast("Preferences Saved!");
                    router.push("/");
                } else {
                    showToast("Error!");
                }
            })
            .catch((err) => {
                setSubmitLoading(false);
                showToast("Error!");
                console.log("err", err);
            });
    };

    return (
        <ParentScrollView _scrollRef={_scrollRef}>
            <Pressable
                style={styles.container}
                onPress={() => {
                    setCloseDropDown(moment().valueOf());
                }}
            >
                <VText style={styles.header}>My Preferences</VText>
                <View style={styles.content}>
                    <SideProfileComp gender={localGender} selected={"Preferences"} />
                    <View
                        style={{
                            width: wp(605),
                            zIndex: 100,
                        }}
                    >
                        {(loading || loadingAns) && <ActivityIndicator size="large" style={{}} color={Colors.black} />}
                        {!(loading || loadingAns) && (
                            <View style={[styles.formSection, { paddingBottom: [2].includes(stepNo) ? 120 : 40 }]}>
                                <PreferenceSteps setStepNo={setStepNo} stepNo={stepNo} prefQuestions={prefQuestions} />
                                {sectionQns?.map((o, idx) => {
                                    switch (o?.questionOptionType) {
                                        case "TILE":
                                            return (
                                                <ImageSelectInput
                                                    answerkey={o?.questionId}
                                                    key={idx}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                />
                                            );
                                        case "COLOR":
                                            return (
                                                <ImageSelectInput
                                                    answerkey={o?.questionId}
                                                    key={idx}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    isColor={true}
                                                />
                                            );
                                        case "SEARCH_DROPDOWN":
                                            return (
                                                <SearchableDropDown
                                                    answerkey={o?.questionId}
                                                    prefAnswers={prefAnswers}
                                                    key={idx}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    index={idx}
                                                    closeDropDown={closeDropDown}
                                                />
                                            );

                                        case "DROPDOWN":
                                            return (
                                                <DropDown
                                                    answerkey={o?.questionId}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    key={idx}
                                                    index={idx}
                                                />
                                            );

                                        case "CHIPS_SINGLE":
                                            return (
                                                <ChipsSelect
                                                    answerkey={o?.questionId}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    key={idx}
                                                />
                                            );

                                        case "CHIPS_MULTI":
                                            return (
                                                <ChipsSelect
                                                    answerkey={o?.questionId}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    key={idx}
                                                    isMulti={true}
                                                />
                                            );

                                        case "OPTION":
                                            return (
                                                <OptionSelect
                                                    answerkey={o?.questionId}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    key={idx}
                                                />
                                            );

                                        case "TEXTAREA":
                                            return (
                                                <TextAreaInput
                                                    answerkey={o?.questionId}
                                                    prefAnswers={prefAnswers}
                                                    setPrefAnswers={setPrefAnswers}
                                                    item={o}
                                                    key={idx}
                                                />
                                            );
                                    }
                                })}
                                {stepNo == prefQuestions?.length && (
                                    <VText
                                        text={`If there are any other styling needs we can help with, please message your personal stylist by hitting the chat icon in the top right corner.\n\n\n\nWelcome to Vêtir!`}
                                        style={{
                                            fontSize: fp(16),
                                            paddingBottom: hp(24),
                                        }}
                                    />
                                )}
                                <View
                                    style={{
                                        flexDirection: "row",
                                        alignItems: "center",
                                    }}
                                >
                                    {stepNo != prefQuestions?.length && (
                                        <>
                                            <Buttons
                                                isInverse
                                                text="Skip All"
                                                noBorder={false}
                                                containerStyle={{
                                                    flex: 1,
                                                    marginRight: spH(12),
                                                }}
                                                onPress={() => {
                                                    setStepNo((prev) => prev + 1);
                                                    _scrollRef?.current?.scrollTo({
                                                        x: 0,
                                                        y: 0,
                                                        animated: true,
                                                    });
                                                }}
                                            />
                                            <Buttons
                                                text="Next"
                                                noBorder={false}
                                                containerStyle={{
                                                    flex: 1,
                                                    marginLeft: spH(12),
                                                }}
                                                onPress={() => {
                                                    setStepNo((prev) => prev + 1);
                                                    _scrollRef?.current?.scrollTo({
                                                        x: 0,
                                                        y: 0,
                                                        animated: true,
                                                    });
                                                }}
                                            />
                                        </>
                                    )}
                                    {stepNo == prefQuestions?.length && (
                                        <>
                                            <Buttons
                                                text="Submit"
                                                noBorder={false}
                                                containerStyle={{
                                                    flex: 1,
                                                }}
                                                loading={submitLoading}
                                                onPress={onSubmit}
                                            />
                                        </>
                                    )}
                                </View>
                            </View>
                        )}
                    </View>
                </View>
            </Pressable>
        </ParentScrollView>
    );
};

export default MyPreferences;

const styles = StyleSheet.create({
    container: {
        backgroundColor: "white",
        paddingTop: spV(32),
        cursor: "auto",
        zIndex: 100,
    },
    header: {
        textAlign: "center",
        fontSize: fp(28),
        fontFamily: "LibreBodoni",
        fontWeight: "350",
        paddingBottom: spV(28),
    },
    content: {
        flexDirection: "row",
        zIndex: 100,
    },
    formSection: {
        zIndex: 100,
        marginHorizontal: spH(47),
        backgroundColor: Colors.grey1,
        padding: spV(40),
        width: wp(605),
        flex: 1,
    },
});
